import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  type Me,
  api,
  call,
  refreshSession,
  setAccessToken,
  setActingCompany,
  setSessionLostHandler,
} from "../api/client";

interface AuthState {
  me: Me | null;
  ready: boolean;
  actingCompany: { id: string; name: string } | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  actAs(company: { id: string; name: string } | null): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);
  const [actingCompany, setActing] = useState<{ id: string; name: string } | null>(null);

  const clear = useCallback(() => {
    setAccessToken(null);
    setActingCompany(null);
    setActing(null);
    setMe(null);
  }, []);

  useEffect(() => {
    setSessionLostHandler(clear);
    // Restore the session from the httpOnly refresh cookie, if any.
    refreshSession()
      .then((t) => setMe(t?.user ?? null))
      .finally(() => setReady(true));
  }, [clear]);

  const value = useMemo<AuthState>(
    () => ({
      me,
      ready,
      actingCompany,
      async login(email, password) {
        const t = await call(api.POST("/api/v1/auth/login", { body: { email, password } }));
        setAccessToken(t.access_token);
        setMe(t.user);
      },
      async logout() {
        await api.POST("/api/v1/auth/logout").catch(() => undefined);
        clear();
      },
      async actAs(company) {
        setActingCompany(company?.id ?? null);
        setActing(company);
        setMe(await call(api.GET("/api/v1/auth/me")));
      },
    }),
    [me, ready, actingCompany, clear],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
